import { toPrecision } from '@curbnturf/helpers';
import { ILandType, RatingType } from '../enums';
import {
  IAccessRoute,
  IActivity,
  IAddress,
  IAmenity,
  IBoondock,
  IBoondockRating,
  IBoondockSignalReception,
  ICoordinateGis,
  ILandOwner,
  IPolicy,
  IPrivacy,
  ISubPhoto,
  IUser,
} from '../interfaces';
import { AccessRoute } from './access-route';
import { Activity } from './activity';
import { BoondockRating } from './boondock-rating';
import { LatLon } from './coordinates';
import { LandOwner } from './land-owner';
import { Noise } from './noise';
import { Privacy } from './privacy';
import { RoadConditionDescribed } from './road-condition-described';
import { User } from './user';

export class Boondock implements IBoondock {
  id?: number;
  syncId?: string;
  user?: IUser;
  available?: boolean;
  name?: string;
  landTypes?: ILandType['type'][];
  privacy?: IPrivacy;
  policies?: IPolicy[];
  maxLengthOfStayInDays?: number;
  noise?: Noise;
  photos?: ISubPhoto[]; // array URL strings
  maxHeight?: number; // ft
  maxWidth?: number; // ft
  maxLength?: number; // ft
  description?: string;
  sizeBuses?: number; // size in number of buses
  sizeCars?: number; // size in number of cars
  ratings?: BoondockRating[];
  rating?: number;
  ratingCount?: number;
  amenities?: IAmenity[];
  signalReception?: IBoondockSignalReception[];
  deleted?: number;
  coordinates?: ICoordinateGis;
  accessRoutes?: IAccessRoute[];
  activities?: IActivity[];
  landOwner?: ILandOwner;
  address?: IAddress;
  agency?: string;
  phone?: string;
  url?: string;
  email?: string;
  source?: string;
  created?: number;
  publishDate?: number;
  potentialHazards?: boolean;
  potentialHazardDescription?: string;

  favoritedUsers?: User[];

  modified?: number;

  get roadCondition(): RoadConditionDescribed {
    if (this.accessRoutes) {
      return Boondock.mergeRoadCondition(this.accessRoutes);
    }

    return new RoadConditionDescribed();
  }

  get nameId(): string {
    return `${this.name} (${this.id})`;
  }
  static mergeRoadCondition(accessRoutes: IAccessRoute[]): RoadConditionDescribed {
    return accessRoutes.reduce(
      (conditionArray: RoadConditionDescribed, route: IAccessRoute) => {
        if (route && route.roadCondition) {
          const roadCondition = route.roadCondition;
          for (const key in conditionArray) {
            if (Object.prototype.hasOwnProperty.call(conditionArray, key)) {
              if (roadCondition[key]) {
                conditionArray[key] = roadCondition[key];
              }
            }
          }
        }
        return conditionArray;
      },
      {
        paved: false,
        pavedDescription: '',
        smoothGravel: false,
        smoothGravelDescription: '',
        poorGravel: false,
        poorGravelDescription: '',
        highClearance: false,
        highClearanceDescription: '',
        offRoad: false,
        offRoadDescription: '',
      } as RoadConditionDescribed,
    );
  }

  constructor(data?: IBoondock) {
    if (data) {
      this.setData(data);
    }
  }

  setData(data: IBoondock): void {
    for (const key in data) {
      if (Object.prototype.hasOwnProperty.call(data, key)) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (this as any)[key] = (data as any)[key];
      }
    }

    if (!this.photos) {
      this.photos = [];
    }

    if (!this.amenities) {
      this.amenities = [];
    }

    if (!this.signalReception) {
      this.signalReception = [];
    }

    // todo: save these numbers so they only have to be calculated when a rating is created
    if (!this.ratings) {
      this.ratings = [];
    } else if (!this.ratingCount && !this.rating) {
      const overallRating = this.ratings.filter((rating) => rating.topic === RatingType.overall);

      this.ratingCount = overallRating.length;
      if (!this.ratingCount) {
        this.rating = 0;
      } else {
        this.rating = toPrecision(
          overallRating.reduce(
            (total, rating: IBoondockRating) =>
              (total = total + (rating && rating.score && rating.score.score ? rating.score.score : 0)),
            0,
          ) / this.ratingCount,
          1,
        );
      }
    }

    if (!this.policies) {
      this.policies = [];
    }

    if (this.privacy) {
      this.privacy = new Privacy(this.privacy);
    }

    this.coordinates = new LatLon(this.coordinates);

    if (!this.activities) {
      this.activities = [];
    } else {
      this.activities = this.activities.map((activity) => new Activity(activity));
    }

    if (!this.landTypes) {
      this.landTypes = [];
    }

    this.landOwner = new LandOwner(this.landOwner);

    this.user = new User(this.user);

    if (this.noise) {
      this.noise = new Noise(this.noise);
    }

    if (this.accessRoutes) {
      this.accessRoutes = this.accessRoutes.map((accessRoute) => new AccessRoute(accessRoute));
    } else {
      this.accessRoutes = [];
    }
  }

  getRating(): number {
    if (this.rating || this.rating === 0) {
      return this.rating;
    }

    return BoondockRating.calculateRating(this.ratings);
  }

  getRatingCount(): number {
    if (this.ratingCount || this.ratingCount === 0) {
      return this.ratingCount;
    }

    return BoondockRating.calculateRatingCount(this.ratings) || 0;
  }

  getFinishedPercent(): number {
    return 1;
  }

  displayLocation(): string {
    if (!this.address || (!this.address.city && !this.address.state)) {
      return '';
    }

    if (!this.address.city) {
      return `${this.address.state}`;
    }

    return `${this.capitalize(this.address.city.toLocaleLowerCase())}, ${this.address.state}`;
  }

  private capitalize(str: string): string {
    return str.replace(/\w\S*/g, (word) => word.replace(/^\w/, (character) => character.toUpperCase()));
  }
}
